import { DateRangePicker } from '@/components/forms/date-range-picker';
import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FilterPill } from '@/components/ui/filter-chips';
import React from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';

export type Period = '7d' | '30d' | 'month' | 'nextMonth' | '90d' | 'all' | 'custom';

type PeriodSelectorProps = {
  period: Period;
  onPeriodChange: (period: Period) => void;
  customRange?: { start: Date; end: Date };
  onCustomRangeChange?: (range: { start: Date; end: Date }) => void;
  /** Períodos a exibir. Se omitido, exibe todos. */
  variants?: Period[];
  /** Renderiza apenas o conteúdo sem Card wrapper. */
  noCard?: boolean;
  /** Rótulo curto acima das pílulas — só faz sentido sem o Card, que já tem título. */
  label?: string;
};

const periodLabels: Record<Period, string> = {
  '7d': '7 dias',
  '30d': '30 dias',
  month: 'Este mês',
  nextMonth: 'Mês que vem',
  '90d': '90 dias',
  all: 'Geral',
  custom: 'Personalizado',
};

export function PeriodSelector({
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
  variants = ['7d', '30d', 'month', '90d', 'all', 'custom'],
  noCard = false,
  label,
}: PeriodSelectorProps) {
  const { watch, setValue } = useForm({
    defaultValues: {
      startDate: customRange?.start || new Date(),
      endDate: customRange?.end || new Date(),
    },
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const customRangeStart = customRange?.start.getTime();
  const customRangeEnd = customRange?.end.getTime();

  React.useEffect(() => {
    if (!customRange) return;
    setValue('startDate', customRange.start);
    setValue('endDate', customRange.end);
  }, [customRange, customRangeStart, customRangeEnd, setValue]);

  const handlePeriodPress = (nextPeriod: Period) => {
    onPeriodChange(nextPeriod);
    if (nextPeriod === 'custom' && onCustomRangeChange) {
      onCustomRangeChange({ start: startDate, end: endDate });
    }
  };

  const content = (
    <>
      {label ? (
        <ThemedText className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          {label}
        </ThemedText>
      ) : null}

      <View className="flex-row flex-wrap gap-2">
        {variants.map((p) => (
          <FilterPill
            key={p}
            label={periodLabels[p]}
            selected={period === p}
            onPress={() => handlePeriodPress(p)}
          />
        ))}
      </View>

      {period === 'custom' && (
        <View className="mt-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({ start, end }) => {
              setValue('startDate', start);
              setValue('endDate', end);
              onCustomRangeChange?.({ start, end });
            }}
          />
        </View>
      )}
    </>
  );

  if (noCard) return content;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <ThemedText className="font-semibold text-lg">Período</ThemedText>
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
